import { useState, useEffect } from 'react'

import { show } from '@shared/api/partners'
import { useAuthentication } from '@shared/contexts/authenticationContext'
import { handleError } from '@shared/utils/apiHelpers'

import { useApiError } from '@legacy/contexts/apiCallErrorContext'
import { ApiSuccess, ApiError } from '@legacy/externalApi/ApiResponse'
import Partner from '@legacy/models/Partner'
import { IPartner } from '@legacy/types/Partner'

interface SuccessCallback {
  (partner: Partner): void
}

/**
 * A hook to fetch the partner info if there is an id provided
 *
 * Exports:
 *   isFetching boolean
 *   fetchPartner function Pass a partner id to trigger the call
 *
 *
 * @param onSuccess function The function to call when (and if) we get a
 *                           successful response. The response body is passed as
 *                           the argument, as a Partner type
 */
const useFetchPartner = (onSuccess: SuccessCallback) => {
  const [id, setId] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<ApiError | undefined>()
  const { getToken } = useAuthentication()
  const { setApiError, clearApiError } = useApiError()

  useEffect(() => {
    let isAborted = false

    const fetchData = async () => {
      const token = getToken()
      if (!id || !token) return
      clearApiError()
      setIsFetching(true)
      setError(undefined)

      const apiResponse = await show(token, id)

      setIsFetching(false)
      if (isAborted) return
      if (!apiResponse.ok) {
        return await handleError({
          apiResponse,
          setApiError,
          setError,
        })
      }

      const response = new ApiSuccess(await apiResponse.json())
      if (onSuccess) onSuccess(new Partner(response.body as IPartner))
    }

    fetchData()

    return function cleanUp(): void {
      isAborted = true
    }
  }, [onSuccess, id, clearApiError, setApiError, getToken])

  return { isFetching, error, fetchPartner: setId }
}

export default useFetchPartner
